import styled from 'styled-components';
import { format } from 'date-fns';
import { body1, colors, spacing } from 'ui/theme';
import { CampaignEvent } from 'modules/campaignsCalendar/hooks';
import { pxToRem } from 'utils/convert';

const DayCell = styled.div`
  position: relative;
  min-height: ${pxToRem(100)};;
  padding: ${spacing.half};
  text-align: center;
  background-color: ${colors.backgroundPrimary};
  border: ${spacing.single} solid ${colors.borderPrimary};
  
  &[data-different-month="true"] {
    color: ${colors.textDisabled};
    background-color: ${colors.backgroundSecondary};
  }
  
  &[data-today="true"] {
    color: ${colors.textInteractive};
    background-color: ${colors.backgroundInteractive};

    &:hover {
      background-color: ${colors.purple20};
    }
  }

  &:hover {
    background-color: ${colors.backgroundPrimaryHover};
  }
`;

const DateNumber = styled.p<{ isToday: boolean }>`
  ${body1}
  font-weight: ${(props) => (props.isToday ? 'bold' : 'normal')};
  color: ${(props) => (props.isToday ? `${colors.textInteractive}` : 'inherit')};
  text-align: center;
`;

const EventIndicator = styled.div`
  display: flex;
  gap: ${spacing.half};
  align-items: center;
`;

export const EventDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${colors.iconInteractiveSubtle};
`;

const MoreIndicator = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  color: ${colors.textSecondary};
  text-align: center;
  padding: ${spacing.half};
  text-decoration: solid underline;
`;

interface CalendarDayProps {
  isDifferentMonth: boolean;
  isToday: boolean;
  events: CampaignEvent[] | undefined;
  day: Date;
}

const MAX_INDICATORS = 10;

export const CalendarDay = ({ isDifferentMonth, isToday, events, day }: CalendarDayProps) => {
  const showMoreIndicator = !!events && events.length > MAX_INDICATORS;
  const visibleEvents = showMoreIndicator ? events.slice(0, MAX_INDICATORS) : events;
  const remainingEventsCount = events ? events.length - MAX_INDICATORS : 0;

  const formattedDate = format(day, 'yyyy-MM-dd');
  const dayOfMonth = format(day, 'd');
  return (
    <DayCell
      data-test={`day-${formattedDate}`}
      key={`day-${formattedDate}`}
      data-different-month={isDifferentMonth}
      data-today={isToday}
    >
      <DateNumber isToday={isToday}>{dayOfMonth}</DateNumber>

      {!!events?.length && (
        <EventIndicator>
          {visibleEvents?.map(({ id }) => (
            <EventDot key={id} data-test="event-dot" />
          ))}
        </EventIndicator>
      )}
      {showMoreIndicator && <MoreIndicator>+ {remainingEventsCount} more</MoreIndicator>}
    </DayCell>
  );
};
